import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {catchError, EMPTY, interval, map, merge, retry, Subject, switchMap, tap} from 'rxjs';
import {ClinicalRegistry} from 'src/domain/clinical-record/clinical-record';
import {ClinicalRecordType} from 'src/domain/enum/clinical-record-type.enum';
import {Patient} from 'src/domain/patient/patient';
import {LocalStorageKeys} from 'src/shared/utils/local-storage-keys.enum';

interface TimelineState
{
    loading: boolean;
    qrCodeToken: string;
    patient: Patient;
    clinicalRegistries: Map<number, ClinicalRegistry[]>;
}

@Injectable({
    providedIn: 'root'
})
export class TimelineService
{
    private readonly httpClient = inject(HttpClient);

    private readonly router = inject(Router);

    private readonly state = signal<TimelineState>({
        loading: false,
        qrCodeToken: localStorage.getItem(LocalStorageKeys.QR_CODE_TOKEN),
        patient: undefined,
        clinicalRegistries: undefined
    });

    public readonly loading = computed(() => this.state().loading);

    public readonly qrCodeToken = computed(() => this.state().qrCodeToken);

    public readonly patient = computed(() => this.state().patient);

    public readonly clinicalRegistries = computed(() => this.state().clinicalRegistries);

    private readonly generateQrCodeToken$ = new Subject();

    private readonly generatingQrCodeToken$ = this.generateQrCodeToken$.pipe(
        switchMap(() =>
        {
            return this.httpClient.post('timeline/generate-qr-code-token', null).pipe(
                tap(({qrCodeToken}: {qrCodeToken: string}) =>
                {
                    localStorage.setItem(LocalStorageKeys.QR_CODE_TOKEN, qrCodeToken);
                    
                    this.state.set({
                        ...this.state(),
                        qrCodeToken
                    });
                })
            );
        })
    );

    private readonly checkSharingValidity$ = computed(() => this.httpClient.get(`timeline/check-timeline-session-validity/${this.qrCodeToken() ?? localStorage.getItem(LocalStorageKeys.QR_CODE_TOKEN)}`));

    private readonly checkIfQrCodeIsValid$ = new Subject();

    private readonly checkingIfQrCodeIsValid$ = this.checkIfQrCodeIsValid$.pipe(
        switchMap(() =>
        {
            return this.checkSharingValidity$().pipe(
                tap(() =>
                {
                    localStorage.setItem(LocalStorageKeys.QR_CODE_TOKEN, this.qrCodeToken());
                    this.router.navigateByUrl('patient-timeline-information');
                }),
                retry({delay: 5000})
            );
        })
    );

    private readonly checkIfSharingStillValid$ = new Subject();

    private readonly checkingIfSharingStillValid$ = this.checkIfSharingStillValid$.pipe(
        switchMap(() =>
        {
            return interval(300000).pipe(
                switchMap(this.checkSharingValidity$),
                catchError(() =>
                {
                    this.closeTimelineSharing();

                    return EMPTY;
                })
            );
        })
    );

    private readonly closeTimelineSharing$ = new Subject();

    private readonly closingTimelineSharing$ = this.closeTimelineSharing$.pipe(
        switchMap(() =>
        {
            return this.httpClient.delete(`timeline/${this.qrCodeToken()}`).pipe(
                tap(() => localStorage.removeItem(LocalStorageKeys.QR_CODE_TOKEN)),
                catchError(() =>
                {
                    localStorage.removeItem(LocalStorageKeys.QR_CODE_TOKEN);
            
                    this.state.set({
                        loading: false,
                        qrCodeToken: null,
                        patient: undefined,
                        clinicalRegistries: undefined
                    });
            
                    return EMPTY;
                })
            );
        })
    );

    private readonly getPatientTimelineInformation$ = new Subject<{filter: string; startDate: string; endDate: string; type: ClinicalRecordType}>();

    private readonly fetchedPatientTimelineInformation$ = this.getPatientTimelineInformation$.pipe(
        switchMap(({filter, startDate, endDate, type}) =>
        {
            return this.httpClient.get(`timeline/${this.qrCodeToken()}?filter=${filter}&startDateFilter=${startDate}&endDateFilter=${endDate}&type=${type}`)
                .pipe(
                    map((responseJSON: any) => ({
                        patient: Patient.fromJSON(responseJSON.patient),
                        clinicalRegistries: responseJSON.clinicalRegistries
                            .map(ClinicalRegistry.fromJSON)
                            .sort((previous: ClinicalRegistry, next: ClinicalRegistry) =>
                                new Date(next?.date ?? next?.startDate).getTime() < new Date(previous?.date ?? previous?.startDate).getTime() ? -1 : 1
                            )
                            .reduce((accumulator: Map<number, ClinicalRegistry[]>, record: ClinicalRegistry) =>
                            {
                                const year = new Date(record?.date ?? record?.startDate).getFullYear();
                                if (!accumulator.has(year))
                                {
                                    accumulator.set(year, []);
                                }
                                accumulator.get(year).push(record);
        
                                return accumulator;
                            }, new Map<number, ClinicalRegistry[]>())
                    })),
                    catchError(() =>
                    {
                        localStorage.removeItem(LocalStorageKeys.QR_CODE_TOKEN);
        
                        this.state.set({
                            loading: false,
                            qrCodeToken: null,
                            patient: undefined,
                            clinicalRegistries: undefined
                        });
                
                        return EMPTY;
                    })
                );
        })
    );

    public constructor()
    {
        merge(
            this.generatingQrCodeToken$,
            this.checkingIfQrCodeIsValid$,
            this.closingTimelineSharing$,
            this.checkingIfSharingStillValid$
        ).pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.state.set({
                    ...this.state(),
                    loading: false
                });
            }
        });

        this.closingTimelineSharing$.pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.state.set({
                    ...this.state(),
                    loading: false,
                    qrCodeToken: null,
                    patient: undefined,
                    clinicalRegistries: undefined
                });

                localStorage.removeItem(LocalStorageKeys.QR_CODE_TOKEN);
            }
        });

        this.fetchedPatientTimelineInformation$.pipe(takeUntilDestroyed()).subscribe({
            next: ({patient, clinicalRegistries}) =>
            {
                this.state.set({
                    ...this.state(),
                    loading: false,
                    patient,
                    clinicalRegistries
                });
            }
        });
    }

    public generateQrCodeToken(): void
    {
        this.state.set({
            ...this.state(),
            loading: true
        });

        this.generateQrCodeToken$.next({});
    }

    public checkIfQrCodeIsValid(): void
    {
        this.checkIfQrCodeIsValid$.next({});
    }

    public checkIfSharingStillValid(): void
    {
        this.checkIfSharingStillValid$.next({});
    }

    public getPatientTimelineInformation(filter = '', startDate = '', endDate = '', type = ClinicalRecordType.ALL): void
    {
        this.state.set({
            loading: true,
            ...this.state()
        });

        this.getPatientTimelineInformation$.next({filter, startDate, endDate, type});
    }

    public closeTimelineSharing(): void
    {
        this.state.set({
            loading: true,
            ...this.state()
        });

        this.closeTimelineSharing$.next({});
    }
}
